/*
 * Monte Vista Conference Week camp: turns a family's day picks into cart lines.
 *
 * Pricing lives in the variant prices, not in discounts (Basic plan, no Functions).
 * Each day has three Seat variants:
 *   First child  $30   first seat that day
 *   Sibling      $15   every other seat that day
 *   Full week    $25   first seat that day, when all five days have a seat
 *
 * Every line is one child on one day, so the daily roster reads straight off orders.
 * Shared by the theme (window.SBTCMVCamp) and the node tests (module.exports).
 */
(function (root) {
  'use strict';

  var DAY_COUNT = 5;
  var SEATS = { FIRST: 'First child', SIBLING: 'Sibling', WEEK: 'Full week' };
  var PRICES = { 'First child': 30, 'Sibling': 15, 'Full week': 25 };

  function cleanDays(days) {
    return (days || []).filter(function (d) {
      return Number.isInteger(d) && d >= 0 && d < DAY_COUNT;
    });
  }

  /**
   * @param {Array<{name: string, grade: string, days: number[]}>} children
   * @returns {string[]} parent-facing error messages, empty when valid
   */
  function validate(children) {
    if (!Array.isArray(children) || children.length === 0) return ['Add at least one camper.'];
    var errors = [];
    children.forEach(function (child, i) {
      var label = 'Camper ' + (i + 1);
      if (!child.name || !String(child.name).trim()) errors.push(label + ' needs a name.');
      if (!child.grade) errors.push(label + ' needs a grade.');
      if (cleanDays(child.days).length === 0) errors.push(label + ' needs at least one day.');
    });
    return errors;
  }

  function isFullWeek(children) {
    for (var day = 0; day < DAY_COUNT; day++) {
      var covered = children.some(function (c) { return cleanDays(c.days).indexOf(day) !== -1; });
      if (!covered) return false;
    }
    return true;
  }

  /**
   * Days are indexes 0 to 4 (Mon to Fri). Children keep the order the parent entered,
   * so the first child listed on a day holds that day's first seat.
   * @returns {{errors: string[], fullWeek: boolean, lines: Array<{day: number, seat: string, child: string, grade: string}>}}
   */
  function buildLines(children) {
    var errors = validate(children);
    if (errors.length) return { errors: errors, fullWeek: false, lines: [] };

    var fullWeek = isFullWeek(children);
    var firstSeat = fullWeek ? SEATS.WEEK : SEATS.FIRST;
    var taken = {};
    var lines = [];

    children.forEach(function (child) {
      var days = cleanDays(child.days).slice().sort();
      days.forEach(function (day) {
        var seat = taken[day] ? SEATS.SIBLING : firstSeat;
        taken[day] = true;
        lines.push({ day: day, seat: seat, child: String(child.name).trim(), grade: child.grade });
      });
    });

    return { errors: [], fullWeek: fullWeek, lines: lines };
  }

  function total(lines) {
    return lines.reduce(function (sum, line) { return sum + PRICES[line.seat]; }, 0);
  }

  /**
   * @param {Array} lines from buildLines
   * @param {Object<string, number>} variantIds keyed "dayIndex|Seat", e.g. "0|First child"
   */
  function toCartItems(lines, variantIds) {
    return lines.map(function (line) {
      var id = variantIds[line.day + '|' + line.seat];
      if (!id) throw new Error('Missing variant for day ' + (line.day + 1) + ' / ' + line.seat);
      return { id: id, quantity: 1, properties: { 'Child name': line.child, 'Grade': line.grade } };
    });
  }

  var api = {
    DAY_COUNT: DAY_COUNT, SEATS: SEATS, PRICES: PRICES,
    validate: validate, buildLines: buildLines, total: total, toCartItems: toCartItems
  };

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api;
  } else {
    root.SBTCMVCamp = api;
  }
})(this);
